import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  type Item, type Language, type Manifest, type Pack, type Picture, type Story, type Voice,
  defaultLanguage, languagesFor, loadManifest, loadPack, pickerLabel, prefs, recording, tallPicture, url, vocabulary, widePicture,
} from "./content";
import { itemAt, path } from "./hit";

/** Plays a recording, or the browser's own voice for a word not recorded yet. */
let playing: HTMLAudioElement | null = null;
function say(src: string | null, text: string, locale: string) {
  playing?.pause();
  speechSynthesis?.cancel();
  if (src) {
    playing = new Audio(src);
    playing.play().catch(() => {});
  } else if ("speechSynthesis" in window) {
    const u = new SpeechSynthesisUtterance(text);
    u.lang = locale;
    speechSynthesis.speak(u);
  }
}

/** Upright and narrow, like a phone: the scene's tall picture when it has one. */
function useUpright() {
  const query = "(max-aspect-ratio: 5/6)";
  const [upright, setUpright] = useState(() => matchMedia(query).matches);
  useEffect(() => {
    const m = matchMedia(query);
    const on = () => setUpright(m.matches);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return upright;
}

interface Quiz {
  words: Item[];
  index: number;
  misses: number;
  /** the wrong thing tapped, outlined for a moment */
  wrong: string | null;
  score: number;
}

export default function StoryPage() {
  const { slug = "", n = "1" } = useParams();
  const [manifest, setManifest] = useState<Manifest | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    loadManifest().then(setManifest, () => setError(true));
  }, []);
  const story = manifest?.stories?.find((s) => s.slug === slug);
  if (error || (manifest && !story)) {
    return (
      <div className="tv-message">
        <p>{error ? "Couldn’t load this story. Check your connection and try again." : "There’s no story here."}</p>
        <Link to="/" className="tv-button">All stories</Link>
      </div>
    );
  }
  if (!manifest || !story) return <div className="tv-story"><div className="tv-loading tv-message">Loading…</div></div>;
  const index = Math.max(0, Math.min(story.scenes.length - 1, Number(n) - 1 || 0));
  // a scene of its own each time, so nothing carries over from the one before
  return <SceneScreen key={story.scenes[index].slug} manifest={manifest} story={story} index={index} />;
}

function SceneScreen({ manifest, story, index }: { manifest: Manifest; story: Story; index: number }) {
  const slug = story.slug;
  const scene = story.scenes[index];
  const navigate = useNavigate();
  const [pack, setPack] = useState<Pack | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [language, setLanguageState] = useState<string>(() => defaultLanguage(manifest));
  const [voice, setVoiceState] = useState<Voice>(prefs.voice());
  const [panel, setPanel] = useState<"words" | "language" | null>(null);
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const upright = useUpright();

  useEffect(() => {
    let live = true;
    loadPack(scene).then((p) => live && setPack(p), (e) => live && setError(String(e)));
    // the next scene too, so turning the page is instant
    const next = story.scenes[index + 1];
    if (next) {
      loadPack(next).then((p) => { new Image().src = url(p.portrait && upright ? p.portrait.image : p.image); }).catch(() => {});
    }
    return () => { live = false; };
  }, [scene, story, index, upright]);

  const languages = useMemo(() => languagesFor(manifest, slug), [manifest, slug]);
  // a language this story doesn't offer (a preview elsewhere) falls back to the first it does
  const lang: Language | undefined = languages.find((l) => l.code === language) ?? languages[0];
  const [explored, setExplored] = useState<Set<string>>(() => prefs.explored(scene.slug));

  const picture: Picture | null = useMemo(() => {
    if (!pack) return null;
    return (upright && tallPicture(pack)) || widePicture(pack);
  }, [pack, upright]);

  // the words in the picture on screen: one only the other picture shows (the pillars in the wide one) can't be found here
  const words = useMemo(() => {
    if (!pack || !picture) return [];
    const outlined = new Set(pack.items.filter((i) => (picture.outlines.get(i.id) ?? []).length).map((i) => i.english));
    return vocabulary(pack.items).filter((w) => outlined.has(w.english));
  }, [pack, picture]);
  const item = pack?.items.find((i) => i.id === selected);
  const translation = item && lang ? item.translations[lang.code] : undefined;

  const explore = useCallback((english: string) => {
    if (!scene) return;
    setExplored((prev) => {
      if (prev.has(english)) return prev;
      const next = new Set(prev).add(english);
      prefs.setExplored(scene.slug, next);
      return next;
    });
  }, [scene]);

  const speak = useCallback((it: Item) => {
    if (!lang) return;
    const t = it.translations[lang.code];
    say(recording(t, voice), t?.text ?? it.english, lang.locale);
  }, [lang, voice]);

  // ---- the quiz: a word is said, the learner finds it

  const askable = useMemo(() => {
    if (!picture || !lang) return [];
    return words.filter((w) => w.translations[lang.code]);
  }, [picture, words, lang]);

  function startQuiz() {
    const shuffled = [...askable].sort(() => Math.random() - 0.5);
    if (!shuffled.length) return;
    setPanel(null);
    setSelected(null);
    setQuiz({ words: shuffled, index: 0, misses: 0, wrong: null, score: 0 });
    speak(shuffled[0]);
  }

  function nextQuestion(q: Quiz, found: boolean) {
    const next = { ...q, index: q.index + 1, misses: 0, wrong: null, score: q.score + (found ? 1 : 0) };
    setQuiz(next);
    setSelected(null);
    if (next.index < next.words.length) setTimeout(() => speak(next.words[next.index]), 700);
  }

  /** Skipped: shown where it is for a moment, then on to the next. */
  function skip(q: Quiz) {
    const wanted = q.words[q.index];
    setSelected(pack?.items.find((i) => i.english === wanted.english && (picture?.outlines.get(i.id) ?? []).length)?.id ?? null);
    setTimeout(() => nextQuestion(q, false), 1800);
  }

  function answer(id: string) {
    if (!quiz || !pack || !lang) return;
    const wanted = quiz.words[quiz.index];
    const tapped = pack.items.find((i) => i.id === id);
    if (!tapped) return;
    if (tapped.english === wanted.english) {
      setSelected(id);
      explore(wanted.english);
      setTimeout(() => nextQuestion(quiz, true), 1500);
    } else {
      const misses = quiz.misses + 1;
      if (misses >= 3) {
        // shown where it is, then on to the next
        setSelected(pack.items.find((i) => i.english === wanted.english && (picture?.outlines.get(i.id) ?? []).length)?.id ?? null);
        setQuiz({ ...quiz, misses, wrong: null });
        setTimeout(() => nextQuestion(quiz, false), 2200);
        return;
      }
      setQuiz({ ...quiz, misses, wrong: id });
      const phrase = manifest.phrases?.tryAgain?.[lang.code];
      const src = phrase?.[voice] ?? phrase?.[voice === "f" ? "m" : "f"];
      say(src ? url(src) : null, "Try again", lang.locale);
      setTimeout(() => setQuiz((q) => (q ? { ...q, wrong: null } : q)), 800);
    }
  }

  // ---- taps on the picture

  const svg = useRef<SVGSVGElement>(null);
  function tap(e: React.PointerEvent<SVGSVGElement>) {
    if (!picture || !pack || !svg.current) return;
    const box = svg.current.getBoundingClientRect();
    const scale = picture.width / box.width;
    const id = itemAt(picture, (e.clientX - box.left) * scale, (e.clientY - box.top) * scale, 14 * scale);
    if (quiz) {
      if (id && quiz.index < quiz.words.length) answer(id);
      return;
    }
    setSelected(id);
    const it = pack.items.find((i) => i.id === id);
    if (it) {
      speak(it);
      explore(it.english);
    }
  }

  function choose(it: Item) {
    const shown = pack!.items.find((i) => i.english === it.english && (picture?.outlines.get(i.id) ?? []).length) ?? it;
    setSelected(shown.id);
    speak(it);
    explore(it.english);
  }

  // ---- keys: arrows turn the page, space says the word again

  const go = useCallback((to: number) => {
    if (to >= 0 && to < story.scenes.length) navigate(`/story/${slug}/${to + 1}`, { replace: true });
  }, [story, slug, navigate]);
  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLElement && e.target.closest("input, select, textarea")) return;
      if (e.key === "ArrowRight" && !quiz) go(index + 1);
      if (e.key === "ArrowLeft" && !quiz) go(index - 1);
      if (e.key === "Escape") { setPanel(null); setQuiz(null); }
    };
    addEventListener("keydown", on);
    return () => removeEventListener("keydown", on);
  }, [go, index, quiz]);

  if (error) {
    return (
      <div className="tv-message">
        <p>Couldn’t load this scene. Check your connection and try again.</p>
        <Link to="/" className="tv-button">All stories</Link>
      </div>
    );
  }

  // every copy of the selected word, outlined
  const highlighted = item && picture
    ? pack!.items.filter((i) => i.english === item.english).flatMap((i) => picture.outlines.get(i.id) ?? [])
    : [];
  const wrongRings = quiz?.wrong && picture ? picture.outlines.get(quiz.wrong) ?? [] : [];
  const asked = quiz && quiz.index < quiz.words.length ? quiz.words[quiz.index] : null;
  const done = quiz && quiz.index >= quiz.words.length;
  const seen = words.filter((w) => explored.has(w.english)).length;

  return (
    <div className={`tv-story ${upright ? "is-upright" : ""}`} dir="ltr">
      <header className="tv-story-bar">
        <Link to="/" className="tv-icon-button" aria-label="All stories">‹</Link>
        <div className="tv-story-title">
          <span className="tv-story-name">{story.title}</span>
          <span className="tv-scene-name">{scene ? `${index + 1}/${story.scenes.length} · ${scene.label}` : ""}</span>
        </div>
        <button className="tv-icon-button" aria-label="Previous scene" disabled={index === 0 || !!quiz} onClick={() => go(index - 1)}>‹</button>
        <button className="tv-icon-button" aria-label="Next scene" disabled={index >= story.scenes.length - 1 || !!quiz} onClick={() => go(index + 1)}>›</button>
      </header>

      <main className="tv-stage">
        {picture && pack ? (
          <div className="tv-picture" style={{ "--ratio": picture.width / picture.height } as React.CSSProperties}>
            <img src={url(picture.image)} alt={pack.alt} draggable={false} />
            <svg
              ref={svg}
              viewBox={`0 0 ${picture.width} ${picture.height}`}
              onPointerUp={tap}
              role="img"
              aria-label="Tap anything in the picture to hear its name"
            >
              {highlighted.length > 0 && <path className="tv-outline" d={path(highlighted)} />}
              {wrongRings.length > 0 && <path className="tv-outline is-wrong" d={path(wrongRings)} />}
            </svg>
          </div>
        ) : (
          <div className="tv-loading" aria-live="polite">Loading…</div>
        )}
      </main>

      <footer className="tv-word-bar">
        {quiz ? (
          <div className="tv-quiz">
            {asked ? (
              <>
                <button className="tv-speak" aria-label="Hear the word again" onClick={() => speak(asked)}>🔊</button>
                <div className="tv-word">
                  <span className="tv-word-text">Find it</span>
                  <span className="tv-word-sub">{quiz.index + 1} of {quiz.words.length}{quiz.misses ? ` · ${3 - quiz.misses} tries left` : ""}</span>
                </div>
                <button className="tv-button is-quiet" onClick={() => skip(quiz)}>Skip</button>
                <button className="tv-button is-quiet" onClick={() => setQuiz(null)}>End</button>
              </>
            ) : done ? (
              <>
                <div className="tv-word">
                  <span className="tv-word-text">{quiz.score} of {quiz.words.length}</span>
                  <span className="tv-word-sub">found</span>
                </div>
                <button className="tv-button" onClick={startQuiz}>Again</button>
                <button className="tv-button is-quiet" onClick={() => setQuiz(null)}>Done</button>
              </>
            ) : null}
          </div>
        ) : (
          <>
            <button className="tv-speak" aria-label="Hear it again" disabled={!item} onClick={() => item && speak(item)}>🔊</button>
            <div className="tv-word" lang={lang?.locale} dir={lang?.direction ?? "ltr"}>
              <span className="tv-word-text">{item ? translation?.text ?? item.english : "Tap anything in the picture"}</span>
              <span className="tv-word-sub" dir="ltr">
                {!item ? "\u00a0" : [translation?.reading, translation?.phonetic, lang && !lang.code.startsWith("en") ? item.english : null].filter(Boolean).join(" · ")}
              </span>
            </div>
            <div className="tv-tools">
              <button className="tv-chip" onClick={startQuiz} disabled={!askable.length} title="Quiz me">Quiz</button>
              <button className="tv-chip" onClick={() => setPanel(panel === "words" ? null : "words")} aria-expanded={panel === "words"}>
                {seen}/{words.length}
              </button>
              <button className="tv-chip" onClick={() => setPanel(panel === "language" ? null : "language")} aria-expanded={panel === "language"}>
                {lang?.label ?? "Language"}
              </button>
            </div>
          </>
        )}
      </footer>

      {panel === "words" && (
        <aside className="tv-panel" aria-label="Words in this scene">
          <div className="tv-panel-head">
            <strong>Words</strong>
            <span>{seen} of {words.length} explored</span>
            <button className="tv-icon-button" aria-label="Close" onClick={() => setPanel(null)}>×</button>
          </div>
          <ul className="tv-word-list">
            {words.map((w) => {
              const t = lang ? w.translations[lang.code] : undefined;
              return (
                <li key={w.id}>
                  <button className={`${explored.has(w.english) ? "is-seen" : ""} ${item?.english === w.english ? "is-on" : ""}`} onClick={() => choose(w)}>
                    <span lang={lang?.locale}>{t?.text ?? w.english}</span>
                    <small>{lang && !lang.code.startsWith("en") ? w.english : t?.phonetic}</small>
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>
      )}

      {panel === "language" && (
        <aside className="tv-panel" aria-label="Language and voice">
          <div className="tv-panel-head">
            <strong>Learn</strong>
            <button className="tv-icon-button" aria-label="Close" onClick={() => setPanel(null)}>×</button>
          </div>
          <div className="tv-voice" role="radiogroup" aria-label="Voice">
            {(["f", "m"] as Voice[]).map((v) => (
              <button key={v} role="radio" aria-checked={voice === v} className={voice === v ? "is-on" : ""}
                onClick={() => { setVoiceState(v); prefs.setVoice(v); }}>
                {v === "f" ? "Woman's voice" : "Man's voice"}
              </button>
            ))}
          </div>
          <ul className="tv-language-list">
            {languages.map((l) => (
              <li key={l.code}>
                <button className={l.code === lang?.code ? "is-on" : ""}
                  onClick={() => { setLanguageState(l.code); prefs.setLanguage(l.code); setPanel(null); if (item) setTimeout(() => say(recording(item.translations[l.code], voice), item.translations[l.code]?.text ?? item.english, l.locale)); }}>
                  {pickerLabel(l)}
                </button>
              </li>
            ))}
          </ul>
        </aside>
      )}
    </div>
  );
}
